import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

function isChunkLoadError(error: Error | null): boolean {
  if (!error) return false
  const msg = `${error.name} ${error.message}`
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    error.name === 'ChunkLoadError'
  )
}

/**
 * Ostatnia linia obrony dla panelu admina.
 *
 * Aplikacja używa `<Routes>` bez data routera, więc nie ma `errorElement` —
 * wyjątek rzucony w renderze odmontowywał CAŁE drzewo React i zostawał biały
 * ekran bez nawigacji, z którego wychodziło się tylko ręczną edycją URL-a albo
 * ubiciem PWA. Wystarczał do tego jeden zły parametr daty w zakładce
 * (`?ws=2026-13-45` → `format(Invalid Date)` → RangeError).
 *
 * Sama przyczyna jest naprawiona walidacją w `calendar.tsx`, ale ekran awaryjny
 * zostaje — kolejny taki błąd nie może kasować dostępu do reszty panelu.
 */
export default class RouteErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Bez zewnętrznego trackera — konsola wystarcza do diagnozy z telefonu
    // podpiętego do Web Inspectora.
    console.error('Błąd renderowania panelu admina:', error, info.componentStack)
  }

  /**
   * React.lazy zapamiętuje ODRZUCONĄ obietnicę importu, więc samo wyczyszczenie
   * stanu granicy błędu nic nie da — komponent poleci tym samym błędem
   * w nieskończoność. Przy nieudanym pobraniu chunku (offline, albo stary klient
   * proszący o plik z poprzedniego builda) jedynym wyjściem jest przeładowanie
   * dokumentu, które zaciąga świeży manifest.
   */
  handleReset = () => {
    if (isChunkLoadError(this.state.error)) {
      window.location.reload()
      return
    }
    this.setState({ error: null })
  }

  override render() {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div className="rounded-card border border-danger/40 bg-danger/5 p-6 max-w-2xl">
        <p className="flex items-center gap-2 text-text-primary font-medium">
          <AlertTriangle className="h-5 w-5 text-danger shrink-0" aria-hidden />
          Ten widok się nie wczytał
        </p>
        <p className="text-sm text-text-muted mt-2">
          {isChunkLoadError(error)
            ? 'Nie udało się pobrać tej części aplikacji. Sprawdź połączenie i spróbuj ponownie — strona przeładuje się i pobierze ją jeszcze raz.'
            : 'Coś poszło nie tak przy wyświetlaniu tej strony. Pozostała część panelu działa — możesz przejść do innej zakładki.'}
        </p>
        <p className="text-xs text-text-muted font-mono mt-3 break-words">
          {error.message}
        </p>
        <div className="flex gap-2 mt-4">
          <a
            href="/admin/calendar"
            className="inline-flex items-center justify-center rounded-action px-4 py-2.5 text-sm font-medium bg-brand-dark text-bg-elevated min-h-[44px]"
          >
            Wróć do kalendarza
          </a>
          <button
            type="button"
            onClick={this.handleReset}
            className="inline-flex items-center justify-center rounded-action px-4 py-2.5 text-sm font-medium border border-border-strong text-text-primary min-h-[44px]"
          >
            {isChunkLoadError(error) ? 'Przeładuj i spróbuj ponownie' : 'Spróbuj ponownie'}
          </button>
        </div>
      </div>
    )
  }
}
